import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Sparkles, Gauge, LayoutDashboard, MessagesSquare, Check, ArrowRight, Users, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDemo } from "../context/DemoContext";
import AIDashboardMock from "../components/AIDashboardMock";
import AskJoyn from "../components/AskJoyn";
import Seo from "../components/Seo";
import { PAGE_SEO } from "../lib/seo";

const meta = [
  { icon: Gauge, accent: "#F05A28" },
  { icon: LayoutDashboard, accent: "#4BB5E7" },
  { icon: MessagesSquare, accent: "#3AAA35" },
];

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }) };

export default function JoynAI() {
  const { openDemo } = useDemo();
  const { t } = useTranslation();
  const features = t("ai.features", { returnObjects: true });

  return (
    <div data-testid="joyn-ai-page">
      <Seo {...PAGE_SEO["/ai"]} path="/ai" />
      <section className="relative pt-32 md:pt-40 pb-24 overflow-hidden">
        <div className="absolute top-16 -right-32 w-[620px] h-[620px] rounded-full opacity-20 blur-3xl anim-spin-slow" style={{ background: "conic-gradient(from 120deg, #4BB5E7, #3AAA35, #D6DF23, #F05A28, #4BB5E7)" }} />
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-14 items-center relative">
          <div>
            <motion.span custom={0} variants={fadeUp} initial="hidden" animate="show" className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] mb-6">
              <Sparkles size={14} className="text-joyn-cyan" /> {t("ai.badge")}
            </motion.span>
            <motion.h1 custom={1} variants={fadeUp} initial="hidden" animate="show" className="font-display font-extrabold text-5xl md:text-6xl lg:text-[4.4rem] tracking-tight leading-[0.95]">
              {t("ai.titleBefore")}<span className="joyn-gradient-text">{t("ai.titleBrand")}</span>
            </motion.h1>
            <motion.p custom={2} variants={fadeUp} initial="hidden" animate="show" className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-xl">{t("ai.desc")}</motion.p>
            <motion.div custom={3} variants={fadeUp} initial="hidden" animate="show" className="mt-8 flex flex-wrap gap-4">
              <button data-testid="ai-hero-demo-btn" onClick={openDemo} className="group inline-flex items-center gap-2 bg-foreground text-white font-semibold px-8 py-4 rounded-full hover:-translate-y-1 transition-transform">{t("ai.cta1")} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" /></button>
              <a href="#ai-features" className="inline-flex items-center gap-2 border border-foreground/20 font-semibold px-8 py-4 rounded-full hover:bg-secondary transition-colors">{t("ai.cta2")}</a>
            </motion.div>
          </div>
          <motion.div custom={2} variants={fadeUp} initial="hidden" animate="show" className="pb-8"><AIDashboardMock /></motion.div>
        </div>
      </section>

      <section id="ai-features" className="border-y border-border bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 text-center">
          <p className="uppercase tracking-[0.2em] text-sm font-bold text-foreground/80">{t("ai.sectionBefore")}<span className="joyn-gradient-text">{t("ai.sectionBrand")}</span></p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 md:px-8 pt-20 -mb-4">
        <div className="text-center mb-8">
          <p className="uppercase tracking-[0.2em] text-xs font-bold text-joyn-cyan mb-3">{t("ai.tryEyebrow")}</p>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight">{t("ai.tryTitle")}</h2>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">{t("ai.tryDesc")}</p>
        </div>
        <AskJoyn />
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-24 space-y-24">
        {features.map((f, i) => {
          const Icon = meta[i].icon; const accent = meta[i].accent;
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55 }}
              className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${i % 2 ? "lg:[direction:rtl]" : ""}`}>
              <div className="[direction:ltr] rounded-3xl p-10 flex items-center justify-center" style={{ background: `linear-gradient(140deg, ${accent}1f, ${accent}08)` }}>
                <span className="inline-flex h-28 w-28 items-center justify-center rounded-3xl text-white shadow-xl" style={{ background: accent }}><Icon size={52} strokeWidth={1.6} /></span>
              </div>
              <div className="[direction:ltr]">
                <div className="flex items-center gap-3 mb-4"><span className="uppercase tracking-[0.16em] text-xs font-bold" style={{ color: accent }}>{f.name}</span><span className="h-1 w-10 rounded-full" style={{ background: accent }} /></div>
                <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight leading-[1.05] mb-4">{f.headline}</h2>
                <p className="text-lg text-muted-foreground leading-relaxed mb-7">{f.intro}</p>
                <ul className="space-y-5">
                  {f.points.map((pt) => (
                    <li key={pt[0]} className="flex items-start gap-4">
                      <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-lg text-white shrink-0" style={{ background: accent }}><Check size={14} /></span>
                      <div><p className="font-semibold leading-snug">{pt[0]}</p><p className="text-sm text-muted-foreground leading-relaxed mt-0.5">{pt[1]}</p></div>
                    </li>
                  ))}
                </ul>
                <div className="mt-7 flex flex-wrap gap-2">
                  {f.outcomes.map((o) => (<span key={o} className="text-xs font-bold uppercase tracking-wide px-3.5 py-1.5 rounded-full text-white" style={{ background: accent }}>{o}</span>))}
                </div>
              </div>
            </motion.div>
          );
        })}
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-24">
        <div className="relative overflow-hidden rounded-3xl bg-foreground text-white p-10 md:p-16">
          <div className="h-1.5 joyn-gradient-bar absolute top-0 inset-x-0" />
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="flex items-center gap-6">
              <span className="hidden sm:inline-flex h-20 w-20 items-center justify-center rounded-full border-2 border-white/20 relative shrink-0"><Users size={34} /><Star size={16} className="absolute -bottom-1 -right-1 text-joyn-orange fill-joyn-orange" /></span>
              <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight leading-[1.05]">{t("ai.closeBefore")}<br /><span className="text-joyn-orange">{t("ai.closeBrand")}</span></h2>
            </div>
            <div>
              <p className="text-white/70 text-lg leading-relaxed mb-6">{t("ai.closeDesc")}</p>
              <div className="flex flex-wrap gap-4">
                <button data-testid="ai-cta-demo-btn" onClick={openDemo} className="inline-flex items-center gap-2 bg-white text-foreground font-semibold px-8 py-4 rounded-full hover:-translate-y-1 transition-transform">{t("ai.closeCta1")} <ArrowRight size={18} /></button>
                <Link to="/products" className="inline-flex items-center gap-2 border border-white/25 font-semibold px-8 py-4 rounded-full hover:bg-white/10 transition-colors">{t("ai.closeCta2")}</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
